import React, { useEffect, useState } from 'react'
import FormControl from '@mui/material/FormControl'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import TextField from '@mui/material/TextField'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Input, Spinner } from 'reactstrap'
import InputAdornment from '@mui/material/InputAdornment'
import SearchDropDownListPaginationComponent from '../../Components/casp/common/SearchDropDownPaginationList'
import { ChangeRequestModule } from '../../constants/Module'
import { Action } from '../../constants/Common'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { getAllDeliverableList } from '../../api/casp/deliverable'
import IndividualDeliverable from './individualDeliverable'
import { DeliverableStatus } from '../../constants/Status'
import { withTranslation } from 'react-i18next'

const EditDeliverableModule = props => {
  const existingDeliverableObject = {
    name: '',
    id: '',
    cost: '',
    weight: '',
    description: '',
    expectedOutput: '',
    startDate: '',
    endDate: ''
  }

  const [isLoading, setIsLoading] = useState(false)
  const [refreshDeliverables, setRefreshDeliverables] = useState(+new Date())
  const [currentDeliverable, setCurrentDeliverable] = useState(null)
  const deliverableUpdateValidation = Yup.object().shape(
    {
      id: Yup.number().integer().required(props.t('Please select a deliverable')),
      name: Yup.string()
        .strict(true)
        .trim(props.t('Must not include leading and trailing spaces'))
        .max(250, props.t('Too Long!'))
        .matches(/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/? ]*$/, props.t("Name should be in proper type"))
        .required(props.t('Please enter name')),
      cost: Yup.number()
        .moreThan(0, props.t('Cost cannot be 0 or negative'))
        .required(props.t('Please Enter cost'))
        .max(
          props.updatedProject?.projectCost?.new ?? props.project.projectCost,
          `${props.t('Cost of Deliverable can not exceed the project cost')} ${
            props.updatedProject?.projectCost?.new ?? props.project.projectCost
          }`
        )
        .test(
          props.t('two decimal places'),
          props.t('Numbers can only have two decimal places'),
          value => {
            return (
              value === null ||
              (value && /^\d+(\.\d{0,2})?$/.test(String(value)))
            )
          }
        ),
      weight: Yup.number()
        .min(0, props.t('Too short!'))
        .integer()
        .required(props.t('Please Enter weight'))
        .max(100, props.t('Weight cannot exceed more than 100')),
      description: Yup.string()
        .max(2000, props.t('Description can not be greater than 2000 characters'))
        .strict(true)
        .trim(props.t('Must not include leading and trailing spaces'))
        .required(props.t('Please Enter description')),
      expectedOutput: Yup.string()
        .strict(true)
        .trim(props.t('Must not include leading and trailing spaces'))
        .max(250, props.t('Too Long!'))
        .required(props.t('Please Enter expected output')),
      startDate: Yup.date()
        .typeError(props.t('Given date is invalid type'))
        .required(props.t('Please Enter start date'))
        .min(
          props.updatedProject?.schedule?.startDate?.new
            ? dayjs(props.updatedProject?.schedule?.startDate?.new)
            : dayjs(props.project?.startDate).format('YYYY-MM-DD'),
          `${props.t('Deliverable start date cannot be less than project start date')} ${dayjs(
            props.updatedProject?.schedule?.startDate?.new ??
              props.project?.startDate
          ).format('MM/DD/YYYY')}`
        )
        .max(
          props.updatedProject?.schedule?.endDate?.new
            ? dayjs(props.updatedProject?.schedule?.endDate?.new)
            : dayjs(props.project?.endDate),
          `${props.t('Deliverable start date cannot be greater than project end date')} ${dayjs(
            props.updatedProject?.schedule?.endDate?.new ??
              props.project.endDate
          ).format('MM/DD/YYYY')}`
        )
        .when('endDate', {
          is: endDate => {
            return !!endDate
          },
          then: schema =>
            schema.max(
              Yup.ref('endDate'),
              props.t('Start date cannot be after End date')
            )
        }),
      endDate: Yup.date()
        .typeError(props.t('Given date is invalid type'))
        .required(props.t('Please Enter end date'))
        .max(
          props.updatedProject?.schedule?.endDate?.new
            ? dayjs(props.updatedProject?.schedule?.endDate?.new)
            : dayjs(props.project?.endDate),
          `${props.t('Deliverable end date cannot be greater than project end date')} ${dayjs(
            props.updatedProject?.schedule?.endDate?.new ??
              props.project.endDate
          ).format('MM/DD/YYYY')}`
        )
        .when('startDate', {
          is: startDate => !!startDate,
          then: schema =>
            schema.min(
              Yup.ref('startDate'),
              props.t('End date cannot be before Start date')
            )
        })
      // projectId: Yup.number().required(),
    },
    ['startDate', 'endDate']
  )

  useEffect(() => {
    if (!currentDeliverable && updateDeliverableFormik.touched?.id) {
      updateDeliverableFormik.resetForm({
        ...existingDeliverableObject
      })
    }
  }, [currentDeliverable])

  const updateDeliverableFormik = useFormik({
    initialValues: {
      ...existingDeliverableObject
    },
    validationSchema: deliverableUpdateValidation,
    validateOnMount: true,
    onSubmit: values => {
      try {
        setIsLoading(true)
        const tempValues = { ...values }
        const tempId = tempValues.id
        delete tempValues.id
        const constructNewValue = {}
        const existingObject = { ...currentDeliverable }
        const existingObjectKeys = Object.keys(existingObject)
        let hasChanges = false
        let isDateValidationFailed = false
        Object.entries(tempValues).forEach(([dataKey, dataValue]) => {
          switch (dataKey) {
            case 'startDate':
            case 'endDate': {
              if (existingObjectKeys.includes(dataKey)) {
                for (const item of props.milestoneCreatedList.filter(
                  item => item.deliverableId === currentDeliverable?.id
                )) {
                  if (
                    dataKey === 'startDate' &&
                    item.estimatedStartDate <
                      dayjs(dataValue).format('YYYY-MM-DD')
                  ) {
                    updateDeliverableFormik.setFieldValue(
                      'startDate',
                      dayjs(currentDeliverable.startDate)
                    )
                    isDateValidationFailed = true
                    return toast.error(
                      `Milestone ${item.name} start date(${dayjs(
                        item.estimatedStartDate
                      ).format(
                        'MM/DD/YYYY'
                      )}) is greater than deliverable requested start date`
                    )
                  }

                  if (
                    dataKey === 'endDate' &&
                    item.estimatedEndDate >
                      dayjs(dataValue).format('YYYY-MM-DD')
                  ) {
                    updateDeliverableFormik.setFieldValue(
                      'endDate',
                      dayjs(currentDeliverable.endDate)
                    )
                    isDateValidationFailed = true
                    return toast.error(
                      `Milestone ${item.name} end date(${dayjs(
                        item.estimatedEndDate
                      ).format(
                        'MM/DD/YYYY'
                      )}) is less than deliverable requested end date`
                    )
                  }
                }

                for (const item of props.milestoneUpdatedList.filter(
                  item => item.deliverableId === currentDeliverable?.id
                )) {
                  if (
                    dataKey === 'startDate' &&
                    item.estimatedStartDate &&
                    item.estimatedStartDate.new <
                      dayjs(dataValue).format('YYYY-MM-DD')
                  ) {
                    updateDeliverableFormik.setFieldValue(
                      'startDate',
                      dayjs(currentDeliverable.startDate)
                    )
                    isDateValidationFailed = true
                    return toast.error(
                      `Milestone ${item.name} start date(${dayjs(
                        item.estimatedStartDate.new
                      ).format(
                        'MM/DD/YYYY'
                      )}) is greater than deliverable requested start date`
                    )
                  }

                  if (
                    dataKey === 'endDate' &&
                    item.estimatedEndDate &&
                    item.estimatedEndDate.new >
                      dayjs(dataValue).format('YYYY-MM-DD')
                  ) {
                    updateDeliverableFormik.setFieldValue(
                      'endDate',
                      dayjs(currentDeliverable.endDate)
                    )
                    isDateValidationFailed = true
                    return toast.error(
                      `Milestone ${item.name} end date(${dayjs(
                        item.estimatedEndDate.new
                      ).format(
                        'MM/DD/YYYY'
                      )}) is less than deliverable requested end date`
                    )
                  }
                }
                if (!dayjs(existingObject[dataKey]).isSame(dayjs(dataValue))) {
                  hasChanges = true
                  constructNewValue[dataKey] = {
                    old: dayjs(existingObject[dataKey]).format('YYYY-MM-DD'),
                    new: dayjs(dataValue).format('YYYY-MM-DD')
                  }
                } else {
                  constructNewValue[dataKey] = dataValue
                }
              }
              break
            }
            case 'name':
            case 'weight':
            case 'description':
            case 'expectedOutput':
            case 'cost': {
              if (existingObjectKeys.includes(dataKey)) {

                if (existingObject[dataKey] !== dataValue) {
                  hasChanges = true
                  constructNewValue[dataKey] = {
                    old: existingObject[dataKey],
                    new: dataValue
                  }
                } else {
                  constructNewValue[dataKey] = dataValue
                }
              }
              break
            }
            default:
              break
          }
        })

        if (hasChanges) {
          constructNewValue.id = tempId

          props.validate(
            {
              module: ChangeRequestModule.DELIVERABLE,
              event: Action.UPDATE
            },
            constructNewValue,
            () => {
              updateDeliverableFormik.resetForm({
                ...existingDeliverableObject
              })
              setCurrentDeliverable(null)
              setIsLoading(false)
              setRefreshDeliverables(+new Date())
            },
            () => {
              setIsLoading(false)
            }
          )
        } else if (!isDateValidationFailed) {
          toast.error(
            'Please provide a change to the deliverable to add it to the change request!'
          )
        }
      } catch (e) {
        // not handle any changes
      } finally {
        setIsLoading(false)
      }
    }
  })

  const handleKeyDownCost = event => {
    if (event.keyCode === 8 || event.keyCode === 13) {
      return
    }
    const value = event.target.value
    const regex = /^\d*\.?\d{0,2}$/

    if (!regex.test(value + event.key)) {
      event.preventDefault()
    }
  }

  const handleKeyDownWeight = event => {
    if (event.keyCode === 8 || event.keyCode === 13) {
      return
    }

    const regex = /^[0-9]+$/

    if (!regex.test(event.key)) {
      event.preventDefault()
    }
  }

  return (
    <>
      <div
        className='editDeliverable_height'
        style={{ height: 'auto', maxHeight: '900px' }}
      >
        <div
          className='dashed_border position-relative  mt-3 mb-5'
          style={{ paddingBottom: '10px' }}
        >
          <div className='row'>
            <div className='col-md-12'>
              <FormControl className='agenda_filter w-100'>
                <SearchDropDownListPaginationComponent
                  label={props.t('Select Deliverable')}
                  key={refreshDeliverables}
                  searchEnabled={false}
                  listCall={getAllDeliverableList}
                  onBlur={() => {
                    if (!updateDeliverableFormik.touched?.id) {
                      updateDeliverableFormik.setFieldTouched('id', true, false)
                    }
                  }}
                  emitItem={newValue => {
                    setCurrentDeliverable(newValue)
                    if (newValue) {
                      updateDeliverableFormik.setFieldValue(
                        'id',
                        newValue ? newValue.id : '',
                        true
                      )
                      const deliverableToSet = {
                        ...newValue,
                        startDate: dayjs(newValue.startDate),
                        endDate: dayjs(newValue.endDate)
                      }
                      delete deliverableToSet.label
                      delete deliverableToSet.key
                      updateDeliverableFormik.setValues({
                        ...deliverableToSet
                      })
                    }
                  }}
                  apiParams={{
                    projectId: props?.project?.id,
                    filterFields: ['currentStatus'],
                    filterInputs: [
                      `${DeliverableStatus.ONGOING},${DeliverableStatus.CONFIRMATION_REJECTED}`
                    ],
                    excludeFilterFields: ['id'],
                    excludeFilterInputs: [
                      props?.deliverableList?.map(_dL => _dL.id).join(',')
                    ]
                  }}
                />
                {currentDeliverable &&
                  updateDeliverableFormik.touched.id &&
                  updateDeliverableFormik.errors.id && (
                    <span className='text-danger'>
                      {updateDeliverableFormik.errors.id}
                    </span>
                  )}
              </FormControl>
            </div>
          </div>
          {currentDeliverable != null ? (
            <>
              <div className='row'>
                <div className='col-md-12 mt-2'>
                  <TextField
                    fullWidth
                    id='outlined-multiline-static'
                    label={props.t('Name')}
                    name='name'
                    disabled={isLoading}
                    value={updateDeliverableFormik.values.name}
                    onChange={updateDeliverableFormik.handleChange}
                    onBlur={updateDeliverableFormik.handleBlur}
                    required
                  />
                  {updateDeliverableFormik.touched.name &&
                    updateDeliverableFormik.errors.name && (
                      <span className='text-danger d-block'>
                        {updateDeliverableFormik.errors.name}
                      </span>
                    )}
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-12 mt-2'>
                  <TextField
                    fullWidth
                    className='form-control'
                    id='deliverable-title-input'
                    label={props.t('Description')}
                    name='description'
                    value={updateDeliverableFormik.values.description}
                    disabled={isLoading}
                    onChange={updateDeliverableFormik.handleChange}
                    onBlur={updateDeliverableFormik.handleBlur}
                    multiline
                    required
                    rows={2}
                  />
                  {updateDeliverableFormik.touched.description &&
                    updateDeliverableFormik.errors.description && (
                      <span className='text-danger'>
                        {updateDeliverableFormik.errors.description}
                      </span>
                    )}
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-12 mt-2'>
                  <TextField
                    fullWidth
                    className='form-control'
                    id='deliverable-title-input'
                    label={props.t('Expected Output')}
                    name='expectedOutput'
                    value={updateDeliverableFormik.values.expectedOutput}
                    disabled={isLoading}
                    onChange={updateDeliverableFormik.handleChange}
                    onBlur={updateDeliverableFormik.handleBlur}
                    multiline
                    required
                    rows={3}
                  />
                  {updateDeliverableFormik.touched.expectedOutput &&
                    updateDeliverableFormik.errors.expectedOutput && (
                      <span className='text-danger'>
                        {updateDeliverableFormik.errors.expectedOutput}
                      </span>
                    )}
                </div>
              </div>
              <div className='row'>
                <div className='col-md-6 mt-2 pe-0 plCurrency currency_small_font'>
                  {/* <TextField
                    fullWidth
                    id='outlined-multiline-static'
                    label={props.t('Cost')}
                    name='cost'
                    type='number'
                    onWheel={e => e.target.blur()}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>SAR</InputAdornment>
                      )
                    }}
                    value={updateDeliverableFormik.values.cost}
                    onKeyDown={handleKeyDownCost}
                    onChange={updateDeliverableFormik.handleChange}
                    onBlur={updateDeliverableFormik.handleBlur}
                    required
                  /> */}
                  <TextField
                    minimumValue="0"
                    id="deliverable-cost"
                    variant="outlined"
                    // label='Deliverable Cost'
                    decimalPlaces={2}
                    value={updateDeliverableFormik.values.cost}
                    // size="small"
                    currencySymbol={props.t("SAR")}
                    fullWidth
                    placeholder={props.t('Cost')}
                    required

                    disabled={isLoading}
                    onChange={(e, newValue) => {
                      updateDeliverableFormik.setFieldValue('cost', newValue === 0 ? '' : newValue, true)
                    }}
                    onBlur={() => {
                      updateDeliverableFormik.setFieldTouched('cost', true, true)
                    }}
                  />
                  {updateDeliverableFormik.touched.cost &&
                    updateDeliverableFormik.errors.cost && (
                      <span className='text-danger d-block'>
                        {updateDeliverableFormik.errors.cost}
                      </span>
                    )}
                </div>
                <div className='col-md-6 mt-2'>
                  <TextField
                    fullWidth
                    id='outlined-multiline-static'
                    label={props.t('Weight')}
                    type='number'
                    name='weight'
                    onWheel={e => e.target.blur()}
                    disabled={isLoading}
                    value={updateDeliverableFormik.values.weight}
                    onKeyDown={handleKeyDownWeight}
                    onChange={updateDeliverableFormik.handleChange}
                    onBlur={updateDeliverableFormik.handleBlur}
                    required
                  />
                  {updateDeliverableFormik.touched.weight &&
                    updateDeliverableFormik.errors.weight && (
                      <span className='text-danger d-block'>
                        {updateDeliverableFormik.errors.weight}
                      </span>
                    )}
                </div>
              </div>
              <div className='row'>
                <div className=' col-md-6 changeRequest_datePicker mt-2 pe-0'>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={['DatePicker']}>
                      <DatePicker
                        label={props.t('Start Date') + ' *'}
                        format='MM/DD/YYYY'
                        name='startDate'
                        disabled={isLoading}
                        value={
                          updateDeliverableFormik?.values?.startDate
                            ?.toString()
                            .trim()
                            ? updateDeliverableFormik.values.startDate
                            : null
                        }
                        on
                        slotProps={{
                          field: {
                            clearable: true,
                            onBlur: _ => {
                              if (!updateDeliverableFormik.touched?.startDate) {
                                updateDeliverableFormik.setFieldTouched(
                                  'startDate',
                                  true,
                                  false
                                )
                              }
                            }
                          },
                          onClear: () => {
                            updateDeliverableFormik.setFieldValue(
                              'startDate',
                              ''
                            )
                          }
                        }}
                        onChange={e => {
                          updateDeliverableFormik.setFieldValue(
                            'startDate',
                            e ? dayjs(e) : ''
                          )
                          if (!updateDeliverableFormik.touched?.startDate) {
                            updateDeliverableFormik.setFieldTouched(
                              'startDate',
                              true,
                              false
                            )
                          }
                        }}
                        required
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                  {updateDeliverableFormik.touched.startDate &&
                    updateDeliverableFormik.errors.startDate && (
                      <span className='text-danger d-block'>
                        {updateDeliverableFormik.errors.startDate}
                      </span>
                    )}
                </div>
                <div className='col-md-6 changeRequest_datePicker mt-2'>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={['DatePicker']}>
                      <DatePicker
                        label={props.t('End Date') + ' *'}
                        format='MM/DD/YYYY'
                        name='endDate'
                        disabled={isLoading}
                        value={
                          updateDeliverableFormik?.values?.endDate
                            ?.toString()
                            .trim()
                            ? updateDeliverableFormik.values.endDate
                            : null
                        }
                        slotProps={{
                          field: {
                            clearable: true,
                            onBlur: _ => {
                              if (!updateDeliverableFormik.touched?.endDate) {
                                updateDeliverableFormik.setFieldTouched(
                                  'endDate',
                                  true,
                                  false
                                )
                              }
                            }
                          },
                          onClear: () => {
                            updateDeliverableFormik.setFieldValue('endDate', '')
                          }
                        }}
                        onChange={e => {
                          updateDeliverableFormik.setFieldValue(
                            'endDate',
                            e ? dayjs(e) : ''
                          )
                          if (!updateDeliverableFormik.touched?.endDate) {
                            updateDeliverableFormik.setFieldTouched(
                              'endDate',
                              true,
                              false
                            )
                          }
                        }}
                        required
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                  {updateDeliverableFormik.touched.endDate &&
                    updateDeliverableFormik.errors.endDate && (
                      <span className='text-danger d-block'>
                        {updateDeliverableFormik.errors.endDate}
                      </span>
                    )}
                </div>
              </div>
              <div className='d-flex justify-content-center align-items-center mt-4'>
                <button
                  className={`add_anotherButton d-flex align-items-center ${
                    isLoading || !updateDeliverableFormik.isValid
                      ? 'bg-secondary'
                      : ''
                  }`}
                  type='button'
                  onClick={updateDeliverableFormik.handleSubmit}
                  disabled={isLoading || !updateDeliverableFormik.isValid}
                >
                  {isLoading ? (
                    <Spinner
                      className='ml-1 me-2'
                      type='border'
                      color='light'
                      size='sm'
                    />
                  ) : (
                    <i className='ri-add-line ml-1' />
                  )}
                  <span className='pe-2 ps-1'>{props.t('Add')}</span>
                </button>
              </div>
            </>
          ) : null}
        </div>

        {props?.deliverableList &&
          props?.deliverableList?.map((dL, index) => {
            return (
              <IndividualDeliverable
                key={index}
                updatedProject={props.updatedProject?.schedule}
                updatedMilestones={props.milestoneUpdatedList.filter(
                  item => item.deliverableId === dL?.id
                )}
                createdMilestones={props.milestoneCreatedList.filter(
                  item => item.deliverableId === dL?.id
                )}
                eachDeliverable={dL}
                action={Action.UPDATE}
                eachDeliverableIndex={index}
                removeItemFromPayload={props?.removeItemFromPayload}
              />
            )
          })}
      </div>
    </>
  )
}

export default withTranslation()(EditDeliverableModule)
